import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Transcription, TranscriptionsService } from '../transcriptions/transcriptions.service';

export interface KeyMetric {
  id: string;
  title: string;
  number: number;
  description: string;
  icon: string;
  borderColor: string;
  iconBackground: string;
  iconColor: string;
}

export interface SummaryStatistic {
  id: string;
  label: string;
  number: number;
  icon: string;
}

export interface DashboardData {
  keyMetrics: KeyMetric[];
  summaryStatistics: SummaryStatistic[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private http: HttpClient,
    private transcriptionsService: TranscriptionsService
  ) { }

  /**
   * Calculate key metrics from transcription data
   */
  getKeyMetrics(): Observable<any[]> {
    return this.transcriptionsService.getAllTranscriptions().pipe(
      map(transcriptions => {
        // Use getDisplayStatus logic to normalize statuses
        const getDisplayStatus = (status: string): string => {
          const normalizedStatus = status ? status.toLowerCase() : '';
          switch (normalizedStatus) {
            case 'completed':
              return 'Completed';
            case 'needs review':
              return 'Needs Review';
            case 'in progress':
            case 'transcribing':
            case 'transcribed':
            case 'analyzing':
              return 'In Progress';
            case 'failed':
              return 'Failed';
            default:
              return 'In Progress';
          }
        };

        // Count by display status
        const statusCounts = transcriptions.reduce((acc, t) => {
          const displayStatus = getDisplayStatus(t.status);
          acc[displayStatus] = (acc[displayStatus] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        return [
          {
            id: 'needs-review',
            number: statusCounts['Needs Review'] || 0
          },
          {
            id: 'in-progress',
            number: statusCounts['In Progress'] || 0
          },
          {
            id: 'completed',
            number: statusCounts['Completed'] || 0
          }
        ];
      })
    );
  }

  /**
   * Calculate summary statistics from transcription data
   */
  getSummaryStatistics(): Observable<any[]> {
    return this.transcriptionsService.getAllTranscriptions().pipe(
      map(transcriptions => {
        const totalTranscriptions = transcriptions.length;

        // Calculate unique patients
        const uniquePatients = new Set(
          transcriptions
            .map(t => t.patientId || t.mrn)
            .filter(id => id != null)
        ).size;

        // Calculate total hours recorded from durations
        // Duration format is typically "HH:MM:SS" or "MM:SS"
        let totalSeconds = 0;
        transcriptions.forEach(t => {
          if (t.duration && t.duration !== 'N/A' && t.duration !== 'Unknown') {
            const parts = t.duration.split(':').map(Number);
            if (parts.length === 3) {
              // HH:MM:SS
              totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
            } else if (parts.length === 2) {
              // MM:SS
              totalSeconds += parts[0] * 60 + parts[1];
            }
          }
        });
        const totalHours = totalSeconds / 3600;

        // Calculate completed percentage (accuracy rate)
        const completedCount = transcriptions.filter(t => {
          const status = t.status?.toLowerCase() || '';
          return status === 'completed';
        }).length;
        const accuracyRate = totalTranscriptions > 0 
          ? Math.round((completedCount / totalTranscriptions) * 100 * 10) / 10 
          : 0;

        return [
          {
            id: 'total-transcriptions',
            number: totalTranscriptions
          },
          {
            id: 'patients',
            number: uniquePatients
          },
          {
            id: 'hours-recorded',
            number: Math.round(totalHours * 10) / 10
          },
          {
            id: 'accuracy-rate',
            number: accuracyRate
          }
        ];
      })
    );
  }

  getDashboardData(): Observable<DashboardData> {
    // This method is kept for backward compatibility but not used
    return this.getKeyMetrics().pipe(
      map(keyMetrics => ({
        keyMetrics: keyMetrics as KeyMetric[],
        summaryStatistics: [] as SummaryStatistic[]
      }))
    );
  }

  getTranscriptions(): Observable<Transcription[]> {
    // Fetch transcriptions from the API
    return this.transcriptionsService.getAllTranscriptions();
  }
} 